/**
 * usePWAInstall — manages the beforeinstallprompt event for custom PWA install UI.
 *
 * Returns:
 * - canInstall: true when the browser supports installation
 * - promptInstall: triggers the native install dialog
 * - isInstalled: true when already installed as PWA
 * - dismissBanner: hides the banner (persists in localStorage)
 * - showBanner: whether to show the install banner
 */
import { useState, useEffect, useCallback, useRef } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const BANNER_DISMISSED_KEY = 'pwa-banner-dismissed'

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isInStandaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone === true)
}

export function usePWAInstall() {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null)
  const [canInstall, setCanInstall] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isIOSDevice, setIsIOSDevice] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(BANNER_DISMISSED_KEY) === '1' } catch { return false }
  })

  useEffect(() => {
    // Check if already installed as standalone PWA
    if (isInStandaloneMode()) {
      setIsInstalled(true)
      return
    }

    // iOS: no beforeinstallprompt, show manual guide
    if (isIOS()) {
      setIsIOSDevice(true)
      setCanInstall(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      deferredPrompt.current = e as BeforeInstallPromptEvent
      setCanInstall(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    // Detect when installed
    const installedHandler = () => setIsInstalled(true)
    window.addEventListener('appinstalled', installedHandler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    // iOS: can't programmatically install, return false to signal manual guide needed
    if (isIOSDevice) return false
    if (!deferredPrompt.current) return false
    await deferredPrompt.current.prompt()
    const { outcome } = await deferredPrompt.current.userChoice
    deferredPrompt.current = null
    setCanInstall(false)
    return outcome === 'accepted'
  }, [isIOSDevice])

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true)
    try { localStorage.setItem(BANNER_DISMISSED_KEY, '1') } catch { /* noop */ }
  }, [])

  return {
    canInstall,
    isInstalled,
    isIOS: isIOSDevice,
    promptInstall,
    dismissBanner,
    showBanner: canInstall && !isInstalled && !bannerDismissed,
  }
}
